import React, { useState } from 'react';
import { TimberItem, BidGroup } from '../../types/bidding';
import { calculateBoardFeet } from '../../utils/timberListParser';
import { formatCurrency, formatBoardFeet } from '../../utils/bidCalculator';
import { Trash2, AlertTriangle } from 'lucide-react';
import './TimberListEditor.css';

interface TimberListEditorProps {
  items: TimberItem[];
  onUpdate: (items: TimberItem[]) => void;
  readOnly?: boolean;
  groups?: BidGroup[];
  onAssignGroup?: (itemId: string, groupId: string | undefined) => void;
}

// Common timber materials
const COMMON_MATERIALS = [
  '#1 GRN DF',
  '#2 GRN DF',
  '#1 KD DF',
  '#2 KD DF',
  '#1 GRN WRC',
  '#2 GRN WRC',
  '#1 GRN HF',
  '#2 GRN HF',
  'SELECT GRN SPF',
  'CONST GRN SPF',
  '#1 PT SYP',
  '#2 PT SYP',
  'Doug Fir',
  'Western Red Cedar',
  'Hemlock Fir',
  'Spruce Pine Fir',
  'Southern Yellow Pine',
];

// Standard lengths for bulk edit
const STANDARD_LENGTHS = ["8'", "10'", "12'", "14'", "16'", "18'", "20'", "22'", "24'"];

const TimberListEditor: React.FC<TimberListEditorProps> = ({
  items,
  onUpdate,
  readOnly = false,
  groups = [],
  onAssignGroup,
}) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [filterGroup, setFilterGroup] = useState<string>('all');
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [showBulkEdit, setShowBulkEdit] = useState(false);

  const handleAddItem = () => {
    const newItem: TimberItem = {
      id: `timber-${Date.now()}`,
      name: '',
      material: '',
      quantity: 1,
      width: 0,
      height: 0,
      realLength: '',
      orderLength: '',
      boardFeet: 0,
      unitPrice: 0,
      totalPrice: 0,
      manuallyEdited: true,
    };
    onUpdate([...items, newItem]);
    setEditingId(newItem.id);
  };

  const handleUpdateItem = (id: string, updates: Partial<TimberItem>) => {
    const updatedItems = items.map(item => {
      if (item.id === id) {
        const updated = { ...item, ...updates, manuallyEdited: true };
        if ('width' in updates || 'height' in updates || 'orderLength' in updates) {
          updated.boardFeet = calculateBoardFeet(updated);
        }
        updated.totalPrice = updated.quantity * updated.unitPrice;
        return updated;
      }
      return item;
    });
    onUpdate(updatedItems);
  };

  const handleDeleteItem = (id: string) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      onUpdate(items.filter(item => item.id !== id));
      setSelectedItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }
  };

  const handleGroupChange = (itemId: string, groupId: string) => {
    if (onAssignGroup) {
      onAssignGroup(itemId, groupId === '' ? undefined : groupId);
    }
  };

  // Toggle item selection
  const toggleItemSelection = (itemId: string) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  // Select/deselect all visible items
  const toggleSelectAll = () => {
    if (selectedItems.size === filteredItems.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(filteredItems.map(item => item.id)));
    }
  };

  // Bulk assign to group
  const handleBulkAssignGroup = (groupId: string) => {
    if (!onAssignGroup || !groupId) return;
    selectedItems.forEach(itemId => {
      onAssignGroup(itemId, groupId === '' ? undefined : groupId);
    });
    setSelectedItems(new Set());
  };

  // Bulk update material
  const handleBulkUpdateMaterial = (material: string) => {
    if (!material) return;
    const updatedItems = items.map(item => {
      if (selectedItems.has(item.id)) {
        return { ...item, material, manuallyEdited: true };
      }
      return item;
    });
    onUpdate(updatedItems);
  };

  // Bulk update length
  const handleBulkUpdateLength = (length: string) => {
    if (!length) return;
    const updatedItems = items.map(item => {
      if (selectedItems.has(item.id)) {
        const updated = { ...item, orderLength: length, realLength: length, manuallyEdited: true };
        updated.boardFeet = calculateBoardFeet(updated);
        updated.totalPrice = updated.quantity * updated.unitPrice;
        return updated;
      }
      return item;
    });
    onUpdate(updatedItems);
  };

  // Bulk delete
  const handleBulkDelete = () => {
    if (selectedItems.size === 0) return;
    if (window.confirm(`Are you sure you want to delete ${selectedItems.size} item(s)?`)) {
      onUpdate(items.filter(item => !selectedItems.has(item.id)));
      setSelectedItems(new Set());
    }
  };

  // Bulk update quantity (add/subtract)
  const handleBulkAdjustQuantity = (adjustment: number) => {
    const updatedItems = items.map(item => {
      if (selectedItems.has(item.id)) {
        const newQty = Math.max(1, item.quantity + adjustment);
        return { 
          ...item, 
          quantity: newQty, 
          totalPrice: newQty * item.unitPrice,
          manuallyEdited: true 
        };
      }
      return item;
    });
    onUpdate(updatedItems);
  };

  const getTotalBoardFeet = () => {
    return filteredItems.reduce((sum, item) => sum + (item.boardFeet * item.quantity), 0);
  };

  const getTotalCost = () => {
    return filteredItems.reduce((sum, item) => sum + item.totalPrice, 0);
  };

  // Filter items by group
  const filteredItems = filterGroup === 'all' 
    ? items 
    : filterGroup === 'unassigned'
      ? items.filter(item => !item.assignedToGroup)
      : items.filter(item => item.assignedToGroup === filterGroup);

  const unassignedCount = items.filter(item => !item.assignedToGroup).length;
  const showGroupColumn = groups.length > 0 && onAssignGroup;
  const hasSelection = selectedItems.size > 0;

  if (items.length === 0 && readOnly) {
    return <div className="timber-list-empty">No timber items</div>;
  }

  return (
    <div className="timber-list-editor">
      <div className="timber-list-header">
        <h3>Timber List ({items.length} items)</h3>
        <div className="header-actions">
          {showGroupColumn && (
            <div className="filter-group">
              <label>Filter:</label>
              <select value={filterGroup} onChange={(e) => setFilterGroup(e.target.value)}>
                <option value="all">All Items ({items.length})</option>
                <option value="unassigned">Unassigned ({unassignedCount})</option>
                {groups.map(g => (
                  <option key={g.id} value={g.id}>
                    {g.name} ({items.filter(i => i.assignedToGroup === g.id).length})
                  </option>
                ))}
              </select>
            </div>
          )}
          {!readOnly && (
            <button className="add-item-btn" onClick={handleAddItem}>
              + Add Item
            </button>
          )}
        </div>
      </div>

      {/* Bulk Edit Toolbar - appears when items are selected */}
      {hasSelection && !readOnly && (
        <div className="bulk-edit-toolbar">
          <div className="bulk-edit-info">
            <input 
              type="checkbox" 
              checked={selectedItems.size === filteredItems.length && filteredItems.length > 0}
              onChange={toggleSelectAll}
            />
            <span><strong>{selectedItems.size}</strong> item(s) selected</span>
            <button className="clear-selection-btn" onClick={() => setSelectedItems(new Set())}>
              Clear
            </button>
          </div>
          
          <div className="bulk-edit-actions">
            {showGroupColumn && (
              <div className="bulk-action">
                <label>Group:</label>
                <select 
                  onChange={(e) => { handleBulkAssignGroup(e.target.value); e.target.value = ''; }}
                  defaultValue=""
                >
                  <option value="" disabled>Assign to...</option>
                  {groups.map(g => (
                    <option key={g.id} value={g.id}>{g.name}</option>
                  ))}
                </select>
              </div>
            )}
            
            <div className="bulk-action">
              <label>Material:</label>
              <select 
                onChange={(e) => { handleBulkUpdateMaterial(e.target.value); e.target.value = ''; }}
                defaultValue=""
              >
                <option value="" disabled>Set material...</option>
                {COMMON_MATERIALS.map(mat => (
                  <option key={mat} value={mat}>{mat}</option>
                ))}
              </select>
            </div>
            
            <div className="bulk-action">
              <label>Length:</label>
              <select 
                onChange={(e) => { handleBulkUpdateLength(e.target.value); e.target.value = ''; }}
                defaultValue=""
              >
                <option value="" disabled>Set length...</option>
                {STANDARD_LENGTHS.map(len => (
                  <option key={len} value={len}>{len}</option>
                ))}
              </select>
            </div>
            
            <div className="bulk-action quantity-action">
              <label>Qty:</label>
              <button onClick={() => handleBulkAdjustQuantity(-1)} title="Decrease quantity">−</button>
              <button onClick={() => handleBulkAdjustQuantity(1)} title="Increase quantity">+</button>
            </div>
            
            <button className="bulk-delete-btn" onClick={handleBulkDelete} title="Delete selected">
              <Trash2 size={16} /> Delete
            </button>
          </div>
        </div>
      )}

      <div className="timber-table-wrapper">
        <table className="timber-table">
          <thead>
            <tr>
              {!readOnly && (
                <th className="checkbox-col">
                  <input 
                    type="checkbox" 
                    checked={selectedItems.size === filteredItems.length && filteredItems.length > 0}
                    onChange={toggleSelectAll}
                    title="Select all"
                  />
                </th>
              )}
              {showGroupColumn && <th className="group-col">Group</th>}
              <th>Name</th>
              <th>Material</th>
              <th>Qty</th>
              <th>Width</th>
              <th>Height</th>
              <th>Order Length</th>
              <th>Board Feet</th>
              <th>Unit Price</th>
              <th>Total</th>
              {!readOnly && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {filteredItems.map(item => (
              <tr 
                key={item.id} 
                className={`
                  ${item.confidence && item.confidence < 0.7 ? 'low-confidence' : ''} 
                  ${!item.assignedToGroup && showGroupColumn ? 'unassigned-row' : ''}
                  ${selectedItems.has(item.id) ? 'selected-row' : ''}
                `}
              >
                {!readOnly && (
                  <td className="checkbox-col">
                    <input
                      type="checkbox"
                      checked={selectedItems.has(item.id)}
                      onChange={() => toggleItemSelection(item.id)}
                    />
                  </td>
                )}
                {showGroupColumn && (
                  <td className="group-col">
                    <select
                      value={item.assignedToGroup || ''}
                      onChange={(e) => handleGroupChange(item.id, e.target.value)}
                      className={`group-select ${!item.assignedToGroup ? 'unassigned' : ''}`}
                    >
                      <option value="">-- Select --</option>
                      {groups.map(g => (
                        <option key={g.id} value={g.id}>{g.name}</option>
                      ))}
                    </select>
                  </td>
                )}
                <td>
                  {readOnly || editingId !== item.id ? (
                    <span onClick={() => !readOnly && setEditingId(item.id)}>{item.name || '-'}</span>
                  ) : (
                    <input
                      type="text"
                      value={item.name}
                      onChange={(e) => handleUpdateItem(item.id, { name: e.target.value })}
                      onBlur={() => setEditingId(null)}
                      autoFocus
                    />
                  )}
                </td>
                <td>
                  {readOnly ? (
                    <span>{item.material || '-'}</span>
                  ) : (
                    <select
                      value={item.material}
                      onChange={(e) => handleUpdateItem(item.id, { material: e.target.value })}
                      className="material-select"
                    >
                      <option value="">-- Select --</option>
                      {COMMON_MATERIALS.map(mat => (
                        <option key={mat} value={mat}>{mat}</option>
                      ))}
                    </select>
                  )}
                </td>
                <td>
                  {readOnly ? (
                    <span>{item.quantity}</span>
                  ) : (
                    <input
                      type="number"
                      value={item.quantity}
                      onChange={(e) => handleUpdateItem(item.id, { quantity: parseInt(e.target.value) || 0 })}
                      min="0"
                    />
                  )}
                </td>
                <td>
                  {readOnly ? (
                    <span>{item.width}"</span>
                  ) : (
                    <input
                      type="number"
                      value={item.width}
                      onChange={(e) => handleUpdateItem(item.id, { width: parseFloat(e.target.value) || 0 })}
                      min="0"
                      step="0.1"
                    />
                  )}
                </td>
                <td>
                  {readOnly ? (
                    <span>{item.height}"</span>
                  ) : (
                    <input
                      type="number"
                      value={item.height}
                      onChange={(e) => handleUpdateItem(item.id, { height: parseFloat(e.target.value) || 0 })}
                      min="0"
                      step="0.1"
                    />
                  )}
                </td>
                <td>
                  {readOnly ? (
                    <span>{item.orderLength}</span>
                  ) : (
                    <input
                      type="text"
                      value={item.orderLength}
                      onChange={(e) => handleUpdateItem(item.id, { orderLength: e.target.value })}
                      placeholder="8'"
                    />
                  )}
                </td>
                <td className="board-feet">
                  {formatBoardFeet(item.boardFeet)}
                </td>
                <td>
                  {readOnly ? (
                    <span>{formatCurrency(item.unitPrice)}</span>
                  ) : (
                    <input
                      type="number"
                      value={item.unitPrice}
                      onChange={(e) => handleUpdateItem(item.id, { unitPrice: parseFloat(e.target.value) || 0 })}
                      min="0"
                      step="0.01"
                    />
                  )}
                </td>
                <td className="total-price">
                  <strong>{formatCurrency(item.totalPrice)}</strong>
                </td>
                {!readOnly && (
                  <td>
                    <button
                      className="delete-item-btn"
                      onClick={() => handleDeleteItem(item.id)}
                      title="Delete item"
                    >
                      ×
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={showGroupColumn ? (readOnly ? 7 : 8) : (readOnly ? 6 : 7)} className="totals-label">
                <strong>Totals ({filteredItems.length} items):</strong>
              </td>
              <td className="board-feet">
                <strong>{formatBoardFeet(getTotalBoardFeet())}</strong>
              </td>
              <td></td>
              <td className="total-price">
                <strong>{formatCurrency(getTotalCost())}</strong>
              </td>
              {!readOnly && <td></td>}
            </tr>
          </tfoot>
        </table>
      </div>

      {items.some(item => item.confidence && item.confidence < 0.7) && (
        <div className="confidence-warning">
          <AlertTriangle size={16} /> Some items have low OCR confidence. Please verify the data.
        </div>
      )}
    </div>
  );
};

export default TimberListEditor;
